<template>
  <div>
    <b-card>
      <b-form @submit.prevent="saveBusinessItem">
        <b-form-group label="Name" label-for="business-item-name">
          <b-form-input
            id="business-item-name"
            v-model.trim="item.name"
            required
            maxlength="120"
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Description" label-for="business-item-description">
          <b-form-textarea
            id="business-item-description"
            v-model="item.description"
            rows="3"
          ></b-form-textarea>
        </b-form-group>

        <b-row>
          <b-col md="4">
            <b-form-group label="Price" label-for="business-item-price">
              <b-form-input
                id="business-item-price"
                v-model.number="item.price"
                type="number"
                min="0"
                step="0.01"
                required
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="SKU" label-for="business-item-sku">
              <b-form-input id="business-item-sku" v-model.trim="item.sku"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Stock Quantity" label-for="business-item-stock">
              <b-form-input
                id="business-item-stock"
                v-model.number="item.stockQuantity"
                type="number"
                min="0"
                step="1"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-checkbox v-model="item.isActive" class="mb-3">
          Active
        </b-form-checkbox>

        <b-form-checkbox v-model="hasOptions" class="mb-3">
          This item has options
        </b-form-checkbox>

        <b-card v-if="hasOptions" class="mb-3" title="Options">
          <div v-for="(option, optionIndex) in optionsDraft" :key="option.clientId" class="border rounded p-3 mb-3">
            <b-row>
              <b-col>
                <b-form-group label="Option name" :label-for="'business-item-option-' + option.clientId">
                  <b-form-input
                    :id="'business-item-option-' + option.clientId"
                    v-model.trim="option.optionLabel"
                    placeholder="e.g. Size"
                    required
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="auto" class="pt-4">
                <b-button size="sm" variant="danger" type="button" @click="removeOption(optionIndex)">
                  Remove option
                </b-button>
              </b-col>
            </b-row>

            <div v-for="(value, valueIndex) in option.optionValues" :key="value.clientId" class="d-flex align-items-center mb-2">
              <b-form-input
                v-model.trim="value.label"
                class="mr-2"
                placeholder="e.g. Small"
                required
              ></b-form-input>
              <b-button size="sm" variant="outline-danger" type="button" @click="removeOptionValue(option, valueIndex)">
                Remove
              </b-button>
            </div>

            <b-button size="sm" variant="outline-primary" type="button" @click="addOptionValue(option)">
              Add value
            </b-button>
          </div>

          <b-button size="sm" variant="outline-primary" type="button" @click="addOption">
            Add option
          </b-button>
        </b-card>

        <b-button type="submit" variant="success" class="mr-2">Save</b-button>
        <b-button :to="{ name: 'businessItemList' }" variant="secondary">Cancel</b-button>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const emptyItem = () => ({
  id: null,
  business: null,
  name: '',
  description: '',
  price: 0,
  sku: null,
  stockQuantity: 0,
  isActive: true,
  options: []
})

export default {
  name: 'BusinessItemForm',
  data () {
    return {
      item: emptyItem(),
      hasOptions: false,
      optionsDraft: [],
      deletedOptionIds: [],
      deletedOptionValueIds: [],
      clientId: 0
    }
  },
  computed: {
    ...mapGetters(['business'])
  },
  methods: {
    ...mapActions([
      'getBusinessItems',
      'postBusinessItem',
      'putBusinessItem',
      'getBusinessItemOptionValues',
      'postBusinessItemOption',
      'putBusinessItemOption',
      'deleteBusinessItemOption',
      'postBusinessItemOptionValue',
      'putBusinessItemOptionValue',
      'deleteBusinessItemOptionValue'
    ]),
    nextClientId () {
      this.clientId += 1
      return 'new-' + this.clientId
    },
    newOption (option) {
      return {
        clientId: this.nextClientId(),
        id: option && option.id ? option.id : null,
        iri: option && option['@id'] ? option['@id'] : null,
        optionLabel: option && option.optionLabel ? option.optionLabel : '',
        optionValues: []
      }
    },
    newOptionValue (value) {
      return {
        clientId: this.nextClientId(),
        id: value && value.id ? value.id : null,
        label: value && value.label ? value.label : ''
      }
    },
    addOption () {
      const option = this.newOption()
      option.optionValues.push(this.newOptionValue())
      this.optionsDraft.push(option)
    },
    addOptionValue (option) {
      option.optionValues.push(this.newOptionValue())
    },
    removeOption (index) {
      const option = this.optionsDraft[index]
      if (option.id) {
        this.deletedOptionIds.push(option.id)
        option.optionValues.forEach(value => {
          if (value.id) this.deletedOptionValueIds.push(value.id)
        })
      }
      this.optionsDraft.splice(index, 1)
    },
    removeOptionValue (option, index) {
      const value = option.optionValues[index]
      if (value.id) this.deletedOptionValueIds.push(value.id)
      option.optionValues.splice(index, 1)
    },
    async hydrateOptions (options) {
      const hydrated = []
      for (const optionData of options || []) {
        const option = this.newOption(optionData)
        const optionIri = option.iri || (option.id ? '/api/business_item_options/' + option.id : null)
        let values = optionData.optionValues || []
        if (values.length && typeof values[0] === 'string') {
          values = await this.getBusinessItemOptionValues({ itemOption: optionIri })
        } else if (!values.length && optionIri) {
          values = await this.getBusinessItemOptionValues({ itemOption: optionIri })
        }
        option.optionValues = values.map(value => this.newOptionValue(value))
        hydrated.push(option)
      }
      this.optionsDraft = hydrated
      this.hasOptions = hydrated.length > 0
    },
    async loadBusinessItem () {
      const id = this.$route.params.iid
      if (!id) {
        this.item = emptyItem()
        this.hasOptions = false
        this.optionsDraft = []
        this.deletedOptionIds = []
        this.deletedOptionValueIds = []
        return
      }

      const items = await this.getBusinessItems({ id: id, pagination: false })
      if (items && items.length) {
        this.item = Object.assign(emptyItem(), items[0])
        await this.hydrateOptions(this.item.options)
      }
    },
    async saveOptions (item) {
      const optionIris = []
      const itemIri = item['@id'] || this.item['@id'] || '/api/business_items/' + item.id
      if (this.hasOptions) {
        for (const option of this.optionsDraft) {
          if (!option.optionLabel) continue
          const savedOption = option.id
            ? await this.putBusinessItemOption({
              id: option.id,
              item: itemIri,
              optionLabel: option.optionLabel
            })
            : await this.postBusinessItemOption({
              item: itemIri,
              optionLabel: option.optionLabel,
              optionValues: []
            })
          option.id = savedOption.id
          option.iri = savedOption['@id'] || option.iri || '/api/business_item_options/' + savedOption.id
          optionIris.push(option.iri)

          for (const value of option.optionValues) {
            if (!value.label) continue
            if (value.id) {
              await this.putBusinessItemOptionValue({
                id: value.id,
                label: value.label,
                itemOption: option.iri
              })
            } else {
              const savedValue = await this.postBusinessItemOptionValue({
                label: value.label,
                itemOption: option.iri
              })
              value.id = savedValue.id
            }
          }
        }
      }

      if (this.hasOptions) {
        for (const valueId of this.deletedOptionValueIds) {
          await this.deleteBusinessItemOptionValue(valueId)
        }
        for (const optionId of this.deletedOptionIds) {
          await this.deleteBusinessItemOption(optionId)
        }
      }

      await this.putBusinessItem({ id: item.id, options: this.hasOptions ? optionIris : [] })
    },
    async saveBusinessItem () {
      const optionIris = this.optionsDraft
        .filter(option => option.id || option.iri)
        .map(option => option.iri || '/api/business_item_options/' + option.id)
      const payload = {
        name: this.item.name,
        description: this.item.description,
        price: this.item.price,
        sku: this.item.sku || null,
        stockQuantity: this.item.stockQuantity,
        isActive: this.item.isActive,
        options: this.hasOptions ? optionIris : []
      }

      let savedItem
      if (this.item.id) {
        savedItem = await this.putBusinessItem(Object.assign({ id: this.item.id }, payload))
      } else {
        payload.business = this.business['@id']
        savedItem = await this.postBusinessItem(payload)
        this.item.id = savedItem.id
      }

      await this.saveOptions(savedItem || this.item)
      if (!this.$route.params.iid && this.item.id) {
        this.$router.replace({ name: 'businessItemForm', params: { iid: this.item.id } })
      }
      this.deletedOptionIds = []
      this.deletedOptionValueIds = []
      this.showSuccess()
    }
  },
  watch: {
    hasOptions (enabled) {
      if (enabled || !this.optionsDraft.length) return
      const optionNames = this.optionsDraft
        .map(option => option.optionLabel)
        .filter(Boolean)
        .join(', ')
      if (!confirm('Please confirm removal of all ' + optionNames + ' options.')) {
        this.$nextTick(() => {
          this.hasOptions = true
        })
        return
      }
      this.optionsDraft.forEach(option => {
        if (option.id) this.deletedOptionIds.push(option.id)
        option.optionValues.forEach(value => {
          if (value.id) this.deletedOptionValueIds.push(value.id)
        })
      })
      this.optionsDraft = []
    },
    '$route' () {
      this.loadBusinessItem()
    }
  },
  created () {
    this.loadBusinessItem()
  }
}
</script>
